import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

/** Where the selected item sits inside its (positioned) container, in px. */
export interface IndicatorBox {
  left: number
  top: number
  width: number
  height: number
  /** False for the first placement and for a resize, so the mark never slides in from nowhere
   *  or lags a window drag; true once the selection itself has moved. */
  animate: boolean
}

const SELECTED = '[aria-selected="true"], [aria-checked="true"]'

const sameBox = (a: IndicatorBox, b: IndicatorBox) =>
  a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height

/**
 * Measures the selected tab or segment, for the one mark (a bar, a thumb) that slides between
 * them. The container must be `relative`: the numbers are offsets inside it, so they also hold
 * while a scrolling strip is scrolled.
 *
 * The mark is placed in a layout effect, before the first paint, so it never starts in the
 * wrong place; it animates only when `selectionKey` changes afterwards. A size change (a font
 * arriving, the window resizing) re-places it without animating.
 */
export function useSlidingIndicator<T extends HTMLElement>(selectionKey: string) {
  const containerRef = useRef<T>(null)
  const [box, setBox] = useState<IndicatorBox | null>(null)
  const placed = useRef(false)

  const measure = useCallback((animate: boolean) => {
    const selected = containerRef.current?.querySelector<HTMLElement>(SELECTED)
    if (selected === null || selected === undefined) {
      setBox(null)
      return
    }
    const next: IndicatorBox = {
      left: selected.offsetLeft,
      top: selected.offsetTop,
      width: selected.offsetWidth,
      height: selected.offsetHeight,
      animate,
    }
    setBox((prev) => (prev !== null && sameBox(prev, next) ? prev : next))
  }, [])

  useLayoutEffect(() => {
    measure(placed.current)
    placed.current = true
  }, [measure, selectionKey])

  useEffect(() => {
    const container = containerRef.current
    if (container === null || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      measure(false)
    })
    observer.observe(container)
    Array.from(container.children).forEach((child) => {
      observer.observe(child)
    })
    return () => {
      observer.disconnect()
    }
  }, [measure, selectionKey])

  return { containerRef, box }
}
